type RecordKey = string | number | symbol;

/**
 * `Object.keys()` with typed return value
 *
 * The actual return value may not correspond to the stated type.
 *
 * Do not use unless you're 100% certain that the object doesn't have additional properties not defined in its type signature.
 *
 * @example
 * TypedKeys({a: 0, b: 1})                // OK
 * TypedKeys({a: 0, b: 1} as {a: number}) // WRONG!!!
 * Both return ["a", "b"] but the type of the second one will be ("a")[] which is incorrect
 */
export function TypedKeys<
  Obj extends Record<RecordKey, unknown>,
  Key extends keyof Obj,
  Ret extends Key[],
>(obj: Obj): Ret {
  return Object.keys(obj) as Ret;
}

/**
 * `Object.entries()` with typed return value
 *
 * The actual return value may not correspond to the stated type.
 *
 * Do not use unless you're 100% certain that the object doesn't have additional properties not defined in its type signature.
 *
 * @example
 * TypedEntries({a: 0, b: "c"})                // OK
 * TypedEntries({a: 0, b: "c"} as {a: number}) // WRONG!!!
 * Both return [["a", 0], ["b", "c"]] but the type of the second one will be ["a", number][] which is incorrect
 */
export function TypedEntries<
  Obj extends Record<RecordKey, unknown>,
  Key extends keyof Obj,
  Val extends Obj[Key],
  Ret extends [Key, Val][],
>(obj: Obj): Ret {
  return Object.entries(obj) as Ret;
}
